import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  NOTIFICATION_AUTHORED_BODY_EVENTS,
  QueryFailureNotice,
  isNotificationEventCode,
  notificationEventKeys,
  resolveIssueNavigation,
} from '@/features/audit-issues';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { canAccessPath } from '@/lib/role-access';
import { useMarkAllRead, useMarkRead, useNotifications, useUnreadCount } from './useNotifications';
import type { NotificationDto } from './types';

/** Localized presentation for one notification row (spec-13 §8.6). */
interface NotificationPresentation {
  title: string;
  body: string | null;
  /** True when `body` is operator-authored content, not a catalog message. */
  isAuthoredNote: boolean;
  /**
   * Unknown event code: the stored raw title/body are compatibility/debug
   * fields only — never the primary message. Rendered exclusively inside a
   * collapsed technical disclosure for internal users (spec-13 §8.6/§11).
   */
  legacy: { title: string; body: string | null } | null;
}

/**
 * The exact target route, or the nearest safe batch section, for a
 * notification's target record (spec-13 §8.6). `null` means there is
 * nothing safe to open — the bell then only marks the item read. This never
 * resolves to `/`.
 */
const pathForTarget = (n: NotificationDto): string | null => {
  if (n.target_record_type === 'exception') {
    if (n.target_batch_id) {
      const resolved = resolveIssueNavigation({
        kind: 'batch_exceptions',
        params: { batch_id: n.target_batch_id, exception_id: n.target_record_id },
      });
      return resolved.ok ? resolved.path : null;
    }
    return `/exceptions?exceptionId=${n.target_record_id}`;
  }
  if (n.target_record_type === 'monthly_batch') {
    const resolved = resolveIssueNavigation({
      kind: 'monthly_batch',
      params: { batch_id: n.target_record_id },
    });
    return resolved.ok ? resolved.path : null;
  }
  return null;
};

/**
 * Header bell: an unread-count badge (polled) and a dropdown of the session
 * user's own notifications. Clicking an item marks it read and, when the
 * target resolves to an allowlisted route, deep-links to it. Auth-only — no
 * RBAC gate (Phase 3 Step 1 scopes the feed to the session user server-side).
 */
export const NotificationBell = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unread = useUnreadCount();
  const list = useNotifications(open);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const unreadCount = unread.data ?? 0;
  const items = list.data ?? [];
  // Round-6 item 3: raw legacy title/body are technical content. The SAME
  // explicit technical authority the shared issue presentation uses
  // (system.admin) gates the disclosure — party type alone never does.
  const canViewTechnicalDetails = hasPermission(user, 'system.admin');

  useEffect(() => {
    if (!open) {
      return;
    }
    const closeWhenOutside = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', closeWhenOutside);
    return () => document.removeEventListener('mousedown', closeWhenOutside);
  }, [open]);

  /**
   * Localize a cataloged system event from its stable code (spec-13 §8.6);
   * `n.title`/`n.body` are legacy compatibility fields and are rendered
   * as-is only for uncataloged event codes.
   */
  const presentNotification = (n: NotificationDto): NotificationPresentation => {
    const code = n.event_type;
    if (isNotificationEventCode(code)) {
      const keys = notificationEventKeys(code);
      const title = t(keys.title, { ns: 'issues' });
      if (NOTIFICATION_AUTHORED_BODY_EVENTS.includes(code) && n.body) {
        return { title, body: n.body, isAuthoredNote: true, legacy: null };
      }
      return { title, body: t(keys.body, { ns: 'issues' }), isAuthoredNote: false, legacy: null };
    }
    // Unknown event code: the generic localized title is the primary
    // message; the raw stored title/body never render as primary text.
    return {
      title: t('efs.issues.generic.title', { ns: 'issues' }),
      body: null,
      isAuthoredNote: false,
      legacy: { title: n.title, body: n.body },
    };
  };

  const handleItem = (n: NotificationDto) => {
    if (!n.read_at) {
      // Fire-and-forget: navigation must not hinge on the read-marking POST.
      markRead.mutate(n.notification_id);
    }
    const path = pathForTarget(n);
    // Defense-in-depth (spec-13 §11): the backend independently authorizes
    // the target, but the bell must not predictably route into a page the
    // current user cannot open. `canAccessPath` matches on the pathname, not
    // the query string, so strip it before checking.
    if (path && canAccessPath(user, path.split('?')[0])) {
      setOpen(false);
      navigate(path);
    }
  };

  return (
    <div
      className="relative"
      ref={ref}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setOpen(false);
        }
      }}
    >
      <Button
        variant="ghost"
        size="icon"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('efs.common.notifications.aria_label')}
        title={t('efs.common.notifications.title')}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white"
            aria-label={t('efs.common.notifications.unread_count', { count: unreadCount })}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-md border border-efs-border bg-efs-bg-panel text-efs-text-primary shadow-md"
        >
          <div className="flex items-center justify-between border-b border-efs-border px-3 py-2">
            <span className="text-sm font-semibold">{t('efs.common.notifications.title')}</span>
            <button
              type="button"
              disabled={unreadCount === 0 || markAllRead.isPending}
              className="text-xs text-efs-accent hover:underline disabled:pointer-events-none disabled:opacity-50"
              onClick={() => {
                void markAllRead.mutateAsync();
              }}
            >
              {t('efs.common.notifications.mark_all_read')}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {list.isLoading ? (
              <div className="px-3 py-6 text-center text-sm text-efs-text-muted" role="status">
                {t('efs.issues.states.loading', { ns: 'issues' })}
              </div>
            ) : list.isError ? (
              <div className="px-3 py-3">
                <QueryFailureNotice error={list.error} onRetry={() => void list.refetch()} />
              </div>
            ) : items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-efs-text-muted">
                {t('efs.common.notifications.empty')}
              </div>
            ) : (
              items.map((n) => {
                const presentation = presentNotification(n);
                // The technical disclosure is a sibling <details>, never
                // nested inside the row button — nesting interactive content
                // inside a <button> is invalid and would make expanding it
                // also trigger navigation (spec-13 §11 permission-gated,
                // collapsed-by-default technical detail).
                const showLegacy = presentation.legacy && canViewTechnicalDetails;
                return (
                  <div
                    key={n.notification_id}
                    className="border-b border-efs-border last:border-b-0"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handleItem(n)}
                      className="flex w-full flex-col gap-0.5 px-3 py-2 text-left outline-none transition-colors hover:bg-efs-bg-subtle focus:bg-efs-bg-subtle"
                    >
                      <span className="flex items-center gap-2">
                        {!n.read_at ? (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-destructive" />
                        ) : null}
                        <span
                          className={
                            n.read_at ? 'text-sm text-efs-text-muted' : 'text-sm font-medium'
                          }
                        >
                          {presentation.title}
                        </span>
                      </span>
                      {presentation.body ? (
                        <span className="line-clamp-2 text-xs text-efs-text-muted">
                          {presentation.isAuthoredNote ? (
                            <span className="font-medium">
                              {t('efs.issues.manual.note_label', { ns: 'issues' })}:{' '}
                            </span>
                          ) : null}
                          {presentation.body}
                        </span>
                      ) : null}
                      {n.target_batch_display ? (
                        <span className="text-[11px] text-efs-text-muted">
                          {n.target_batch_display}
                        </span>
                      ) : null}
                      <span className="text-[11px] text-efs-text-muted">
                        {new Date(n.created_at).toLocaleString(i18n.language)}
                      </span>
                    </button>
                    {showLegacy ? (
                      <details className="px-3 pb-2 text-xs text-efs-text-muted">
                        <summary className="cursor-pointer select-none">
                          {t('efs.issues.notifications.legacy_label', { ns: 'issues' })}
                        </summary>
                        <div className="mt-1 space-y-0.5">
                          <p>{presentation.legacy?.title}</p>
                          {presentation.legacy?.body ? <p>{presentation.legacy.body}</p> : null}
                        </div>
                      </details>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
